import { createFileRoute } from "@tanstack/react-router";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD } from "@/lib/structured-data";
import { MarketingShell, MarketingHero, FeatureGrid, CtaBand } from "@/components/MarketingShell";

export const Route = createFileRoute("/invoicing-software")({
  component: InvoicingMarketingPage,
  head: () => ({
    meta: [
      { title: "Security Guard Invoicing & Billing Software — MissionReady" },
      { name: "description", content: "Auto-generate invoices from guard shifts and time entries. Per-site rate cards, overtime multipliers, QuickBooks and Xero exports, and branded client PDFs." },
      ...socialMeta({
        title: "Security Guard Invoicing & Billing Software — MissionReady",
        description: "Turn schedule hours into accurate, audit-ready invoices — automatically. Built for security operators.",
        url: "https://missionready.dev/invoicing-software",
      }),
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/invoicing-software" }],
    scripts: [
      ...ORG_WEBSITE_JSONLD,{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "MissionReady Invoicing",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description: "Automated invoicing for security operators — generate invoices from shifts and time entries with per-site rates, overtime multipliers, and accounting exports.",
        url: "https://missionready.dev/invoicing-software",
        brand: { "@type": "Brand", name: "MissionReady" },
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD", url: "https://missionready.dev/pricing" },
        featureList: [
          "Auto-generate invoices from schedule",
          "Per-site and per-service-type rate cards",
          "Overtime multipliers per contract",
          "Flat monthly retainers",
          "QuickBooks and Xero CSV export",
          "Branded client PDF invoices",
          "Timezone- and DST-safe billing cycles",
          "Idempotent cron with backfill",
        ],
      }),
    }],
  }),
});

const FEATURES = [
  { icon: "⚡", title: "Auto-generate from shifts", desc: "Cron rolls each contract's billing cycle and drafts invoices from time entries — no manual data entry." },
  { icon: "📐", title: "Per-contract rate cards", desc: "Store per-site and per-service-type rates on each contract. Flat retainers and hourly billing live side by side." },
  { icon: "⏱️", title: "Overtime that matches reality", desc: "Set an OT threshold and multiplier per contract. Hours are aggregated per officer per ISO week, then split into regular and OT buckets." },
  { icon: "🌍", title: "Timezone & DST safe", desc: "Billing periods compute in the contract's local timezone with calendar-anchored cycles — late cron ticks never drift your cadence." },
  { icon: "📊", title: "QuickBooks & Xero exports", desc: "Bulk-select invoices and export accountant-ready CSVs with GL codes mapped from your contract or site rate cards." },
  { icon: "📄", title: "Branded client PDFs", desc: "Export polished invoices with your logo and bill-to details, ready to send to clients or attach to a portal." },
];

function InvoicingMarketingPage() {
  return (
    <MarketingShell>
      <MarketingHero
        eyebrow="Invoicing & Billing"
        title={<>Turn shifts into <span className="text-primary">audit-ready invoices.</span></>}
        subtitle="Automate billing from the same schedule you already run. Per-site rates, overtime rules, GL exports, and branded PDFs — built for security operators."
      />
      <FeatureGrid items={FEATURES} heading="Billing & invoicing capabilities" />
      <CtaBand title="Stop billing in spreadsheets" subtitle="Start free for 30 days. Connect your first contract in minutes." />
    </MarketingShell>
  );
}
